import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { OPPORTUNITIES, RURAL_REVIEW_DATE } from "@/data/rural";

const FIELDS = [
  ["What it involves", "involves"],
  ["How it can generate income", "income"],
  ["Does the agritourism framework apply?", "agritourism"],
  ["Zoning & development approvals", "approvals"],
  ["Setup & operating costs", "costs"],
  ["Environmental & practical considerations", "environmental"],
];

const RuralOpportunity = () => {
  const { slug } = useParams();
  const o = OPPORTUNITIES.find((x) => x.slug === slug);

  if (!o) {
    return (
      <div className="py-32 text-center" data-testid="opportunity-not-found">
        <h1 className="font-serif text-3xl text-teal">Opportunity not found</h1>
        <Link to="/rural" className="mt-4 inline-block text-sm text-teal underline underline-offset-4">
          Back to Rural Land
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="opportunity-page">
      <Seo title={o.title} description={o.tagline} />
      <section className="bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <Link
            to="/rural"
            data-testid="opportunity-back"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Rural Land
          </Link>
          <FadeIn>
            <h1 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-teal tracking-tight">{o.title}</h1>
            <p className="mt-4 text-base md:text-lg text-charcoal/75 leading-relaxed">{o.tagline}</p>
          </FadeIn>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {FIELDS.map(([label, key], i) => (
            <FadeIn key={key} delay={i * 0.04}>
              <div data-testid={`opportunity-field-${key}`}>
                <Eyebrow>{label}</Eyebrow>
                <p className="mt-2.5 text-charcoal/80 leading-relaxed">{o[key]}</p>
              </div>
            </FadeIn>
          ))}

          <FadeIn>
            <div className="bg-sand-light/50 border border-cloud p-6" data-testid="opportunity-sources">
              <Eyebrow>Authoritative sources</Eyebrow>
              <ul className="mt-3 space-y-2">
                {o.sources.map((s) => (
                  <li key={s.url}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-teal underline underline-offset-4 hover:text-teal-dark"
                    >
                      {s.label} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-charcoal/55">Legislative content last reviewed {RURAL_REVIEW_DATE}.</p>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/rural/explorer"
                data-testid="opportunity-cta-explorer"
                className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-6 py-3 rounded-full hover:bg-teal-dark transition-colors"
              >
                Explore my land's potential
              </Link>
              <Link
                to="/book"
                data-testid="opportunity-cta-book"
                className="inline-flex items-center gap-2 border border-teal text-teal text-sm font-medium px-6 py-3 rounded-full hover:bg-teal hover:text-ivory transition-colors"
              >
                Speak to a Planner <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default RuralOpportunity;
